import React, { useState } from 'react';

/**
 * Month-over-month building blocks shared by the monthly views
 * (Search Intel → Homepage searches → Month over month, Analytics → Month
 * over month): number formats, UTC month labels, the ▲▼ change badge and a
 * column chart with one column (or a group) per month.
 *
 * Months are UTC calendar months ('YYYY-MM'); the servers bucket the same way.
 */

export const rate = (n: number | null | undefined, d: number | null | undefined): number | null =>
  n === null || n === undefined || !d ? null : n / d;
export const fmtN = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString());
/** 0–1 fraction -> "3.4%" (one decimal under 10%). */
export const fmtP = (v: number | null | undefined) => {
  if (v === null || v === undefined) return '—';
  const p = v * 100;
  return `${p < 10 && p > 0 ? p.toFixed(1) : Math.round(p)}%`;
};
/** Minor units (paise, cents) -> "₹12,345" / "$1,234"; whole units only. */
export const fmtMoney = (minor: number | null | undefined, currency = 'INR') => {
  if (minor === null || minor === undefined) return '—';
  const sym: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', CHF: 'CHF ' };
  const major = Math.round(minor / 100);
  return `${sym[currency] ?? `${currency} `}${major.toLocaleString(currency === 'INR' ? 'en-IN' : 'en-GB')}`;
};
export const monthLabel = (m: string, short = false) => {
  const d = new Date(`${m}-01T00:00:00Z`);
  return isNaN(d.getTime()) ? m
    : d.toLocaleDateString('en-GB', { month: 'short', year: short ? '2-digit' : 'numeric', timeZone: 'UTC' });
};
export const utcStamp = (s: string | null | undefined) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s
    : `${d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} UTC`;
};
export const utcDay = (s: string) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};
/** The month's first and last UTC day, for a from / to filter. */
export const monthDays = (m: string): [string, string] => {
  const [y, mo] = m.split('-').map(Number);
  return [`${m}-01`, new Date(Date.UTC(y, mo, 0)).toISOString().slice(0, 10)];
};

/** Change against the comparison month: % for counts (and money), percentage
 *  points for rates; nothing when either side is missing, "new" from zero.
 *  `unlike` says why the two months were not counted the same way: the
 *  change is then shown grey and marked ~, since most of it is the switch. */
export const Delta: React.FC<{
  cur: number | null | undefined; prev: number | null | undefined; kind: 'count' | 'rate';
  vs?: string; unlike?: string;
}> = ({ cur, prev, kind, vs, unlike }) => {
  if (cur === null || cur === undefined || prev === null || prev === undefined) return null;
  let text: string; let dir: number;
  if (kind === 'rate') {
    const pp = (cur - prev) * 100;
    dir = Math.abs(pp) < 0.05 ? 0 : Math.sign(pp);
    text = `${pp > 0 ? '+' : ''}${pp.toFixed(1)} pp`;
  } else if (prev === 0) {
    if (cur === 0) return null;
    dir = 1; text = 'new';
  } else {
    const d = ((cur - prev) / prev) * 100;
    dir = Math.round(d) === 0 ? 0 : Math.sign(d);
    text = `${d > 0 ? '+' : ''}${Math.abs(d) < 10 ? d.toFixed(1) : Math.round(d)}%`;
  }
  if (unlike) {
    return (
      <span className="text-[10px] whitespace-nowrap text-grayscale-1100 cursor-help"
        title={`vs ${vs || 'the month before'} — not like for like: ${unlike}`}>~ {text}</span>
    );
  }
  const look = dir > 0 ? 'text-green-700' : dir < 0 ? 'text-red-600' : 'text-grayscale-1100';
  return (
    <span className={`text-[10px] whitespace-nowrap ${look}`} title={vs ? `vs ${vs}` : undefined}>
      {dir > 0 ? '▲' : dir < 0 ? '▼' : '='} {text}
    </span>
  );
};

/** One metric the chart can show: a single column per month, or a group
 *  (`series`) side by side. `kind` picks the number format. */
export type TrendMetric = {
  key: string; label: string; kind: 'count' | 'rate' | 'money';
  series?: { key: string; label: string; tone: string }[];
  note?: string;
};

/** Columns per month with a metric toggle; clicking a month selects it. The
 *  month still running is drawn paler and marked *. */
export const MonthTrend: React.FC<{
  rows: Array<Record<string, any> & { month: string; running?: boolean }>;
  metrics: TrendMetric[];
  selected: string; onSelect: (m: string) => void;
  /** The metric shown first. */
  initial?: string;
}> = ({ rows, metrics, selected, onSelect, initial }) => {
  const [metric, setMetric] = useState(initial || metrics[0]?.key || '');
  const spec = metrics.find(m => m.key === metric) || metrics[0];
  if (!spec) return null;
  const series = spec.series?.length ? spec.series : [{ key: spec.key, label: spec.label, tone: 'bg-primary-500' }];
  const val = (r: any, k: string): number | null => (typeof r[k] === 'number' ? r[k] : null);
  const max = Math.max(spec.kind === 'rate' ? 0.0001 : 1, ...rows.flatMap(r => series.map(s => val(r, s.key) ?? 0)));
  const show = (v: number | null) => (spec.kind === 'rate' ? fmtP(v)
    : spec.kind === 'money' ? (v === null ? '—' : fmtMoney(v)) : fmtN(v));
  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 flex-wrap">
        {metrics.map(m => (
          <button key={m.key} type="button" onClick={() => setMetric(m.key)}
            className={`py-1 px-2.5 rounded-full text-xs font-grotesk whitespace-nowrap ${spec.key === m.key
              ? 'bg-primary-500 text-grayscale-100 font-bold'
              : 'border border-grayscale-400 text-grayscale-1200 hover:bg-grayscale-200'}`}>
            {m.label}
          </button>
        ))}
      </div>
      {(series.length > 1 || spec.note) && (
        <div className="flex flex-wrap gap-3 text-[11px] text-grayscale-1100">
          {series.length > 1 && series.map(s => (
            <span key={s.key}><span className={`inline-block w-2 h-2 rounded-sm mr-1 ${s.tone}`} />{s.label}</span>
          ))}
          {spec.note && <span>{spec.note}</span>}
        </div>
      )}
      <div className="flex gap-2">
        <div className="flex flex-col justify-between text-[10px] text-grayscale-1100 text-right w-14 shrink-0 h-44 pt-4">
          <span>{show(max)}</span><span>0</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative h-44 border-b border-grayscale-200">
            <div aria-hidden className="absolute inset-x-0 top-4 border-t border-dashed border-grayscale-200" />
            <div className="absolute inset-0 flex items-end gap-2">
              {rows.map(r => (
                <button key={r.month} type="button" onClick={() => onSelect(r.month)}
                  title={`${monthLabel(r.month)}: ${series.map(s => `${s.label} ${show(val(r, s.key))}`).join(' · ')}`}
                  className={`flex-1 h-full flex items-end justify-center gap-[3px] px-1 rounded-t ${r.month === selected ? 'bg-primary-100' : 'hover:bg-grayscale-200/60'}`}>
                  {series.map(s => {
                    const v = val(r, s.key);
                    return (
                      <div key={s.key} className="flex-1 max-w-[2.5rem] h-full flex flex-col justify-end items-center">
                        {rows.length <= 14 && <span className="text-[10px] text-grayscale-1200 leading-tight mb-0.5">{show(v)}</span>}
                        <div className={`w-full rounded-t ${s.tone} ${r.running ? 'opacity-60' : ''}`}
                          style={{ height: v ? `max(2px, calc((100% - 1rem) * ${v / max}))` : 0 }} />
                      </div>
                    );
                  })}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 text-[10px] text-grayscale-1100 mt-1">
            {rows.map(r => (
              <span key={r.month} className={`flex-1 text-center ${r.month === selected ? 'font-bold text-grayscale-1300' : ''}`}>
                {monthLabel(r.month, true)}{r.running ? '*' : ''}
              </span>
            ))}
          </div>
        </div>
      </div>
      {rows.some(r => r.running) && <div className="text-[10px] text-grayscale-1100">* month to date (paler column)</div>}
    </div>
  );
};
